/**
 * Promo Detail Page
 * 
 * Detail page for viewing promo information with tabs:
 * - Overview: Display all promo details in cards
 * - Usage: Display usage statistics
 * - Audit Logs: Display audit logs filtered by promoId
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Alert, Nav, Modal, Form, Button } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';

// Common components
import Breadcrumbs from '../../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../../Common/ShimmerLoader';
import { SuccessModal } from '../../../../Common/SuccessModal';
import { PromoStatusBadge } from '../../../../Common/BadgeStyles';
import { DeleteModal } from '../../../../Common/DeleteModal';
import { useSelector } from 'react-redux';
import { hasAnyPermission, type User } from '../../../../utils/roleBasedAccess';
import secureApi from '../../../../utils/secureApi';

// API and types
import { getPromoById, updatePromoStatus } from '../../../../api/promos';
import { Promo, PromoStatus, PromoVisibilityType } from '../../../../api/promos/types';

// Tab components
import OverviewTab from './components/OverviewTab';
import { UsageTab, AuditLogsTab } from '../../../../components/promos/detailTabs';
import TablePaginationFooter from '../../../../components/ui/molecules/TablePaginationFooter';

// Styles
import './PromoDetail.css';

const PromoDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, ['promo.admin']);
  
  const [promo, setPromo] = useState<Promo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);
  const [assignForm, setAssignForm] = useState({
    userId: '',
    usageLimit: 1,
    expiresAt: '',
  });
  const [assignmentRows, setAssignmentRows] = useState<Array<{
    id: string;
    assignmentId: string;
    userId: string;
    fullName?: string;
    email?: string;
    phoneNumber?: string;
    status: string;
    used: number;
    usageLimit: number;
    expiresAt: string;
  }>>([]);

  // Assignment pagination state
  const [assignPage, setAssignPage] = useState(1);
  const [assignPageSize, setAssignPageSize] = useState(50);
  const [assignTotalCount, setAssignTotalCount] = useState(0);
  const [assignTotalPages, setAssignTotalPages] = useState(1);

  // Revoke confirmation modal state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeAssignmentId, setRevokeAssignmentId] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [hoveredActionRowId, setHoveredActionRowId] = useState<string | null>(null);
  const [actionDropdownPosition, setActionDropdownPosition] = useState<{ x: number; y: number } | null>(null);
  const actionIconRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const actionDropdownRef = useRef<HTMLDivElement | null>(null);

  const formatExpiresAt = (value?: string) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleActionIconClick = useCallback(
    (rowKey: string, e: React.MouseEvent<HTMLSpanElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const dropdownWidth = 180;
      const viewportWidth = window.innerWidth;
      const xClamped = Math.max(8, Math.min(rect.right - dropdownWidth, viewportWidth - dropdownWidth - 8));
      const y = rect.bottom + 6;

      actionIconRefs.current[rowKey] = e.currentTarget;
      setHoveredActionRowId((prev) => {
        // Toggle if the same row is clicked again
        if (prev === rowKey) {
          setActionDropdownPosition(null);
          return null;
        }
        setActionDropdownPosition({ x: xClamped, y });
        return rowKey;
      });
    },
    [],
  );

  // Close dropdown when clicking outside both the CTA icon and the dropdown.
  useEffect(() => {
    if (!hoveredActionRowId) return;

    const onDocumentMouseDown = (ev: MouseEvent) => {
      const target = ev.target as Node | null;
      if (!target) return;

      const iconEl = actionIconRefs.current[hoveredActionRowId];
      const dropdownEl = actionDropdownRef.current;

      if (iconEl && iconEl.contains(target)) return;
      if (dropdownEl && dropdownEl.contains(target)) return;

      setHoveredActionRowId(null);
      setActionDropdownPosition(null);
    };

    document.addEventListener('mousedown', onDocumentMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown, true);
  }, [hoveredActionRowId]);

  // Allow navigation to specific tab from other pages (e.g., Manage assignments action)
  useEffect(() => {
    const requestedTab = (location.state as any)?.activeTab || (location.state as any)?.tab;
    if (requestedTab) {
      setActiveTab(String(requestedTab));
    }
  }, [location.state]);

  const fetchAssignedUsers = useCallback(async (page = assignPage, pageSize = assignPageSize) => {
    if (!promo?.id) return;
    try {
      setAssignError(null);
      const response = await secureApi.get(
        `/api/v1/promo/${promo.id}/assigned-users`,
        { params: { pageNum: page, pageSize } }
      );

      const data = response?.data?.data ?? response?.data ?? {};
      const results = data?.results ?? response?.data?.results ?? [];
      const totalCount = data?.totalCount ?? data?.total ?? data?.count ?? response?.data?.totalCount ?? results.length;
      const totalPages = data?.totalPages ?? data?.pages ?? response?.data?.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));

      setAssignTotalCount(totalCount);
      setAssignTotalPages(totalPages);
      setAssignmentRows(
        results.map((row: any) => ({
          assignmentId:
            row.assignmentId ||
            row._id ||
            row.id ||
            `${row.userId}-${row.assignedAt || row.expiresAt || Date.now()}`,
          id:
            row.assignmentId ||
            row._id ||
            row.id ||
            `${row.userId}-${row.assignedAt || row.expiresAt || Date.now()}`,
          userId: row.userId,
          fullName: row.fullName,
          email: row.email,
          phoneNumber: row.phoneNumber,
          status: row.status || 'ACTIVE',
          used: row.usedCount ?? row.used ?? 0,
          usageLimit: row.usageLimit ?? row.usage_limits ?? 0,
          expiresAt: row.expiresAt || '',
        })),
      );
    } catch (err: any) {
      console.error('Failed to fetch assigned promo users:', err);
      setAssignError(err?.message || 'Failed to fetch assigned users');
      setAssignmentRows([]);
    }
  }, [promo?.id, assignPage, assignPageSize]);

  const handleConfirmRevoke = useCallback(async () => {
    if (!revokeAssignmentId) return;
    try {
      setIsRevoking(true);
      setAssignError(null);

      // revoke endpoint (assignmentId is taken from assigned-users API result)
      await secureApi.post(
        `/api/v1/promo/assignments/${revokeAssignmentId}/revoke`
      );

      setSuccessTitle('Assignment Revoked');
      setSuccessMessage('Promo assignment revoked successfully.');
      setShowSuccessModal(true);

      setShowRevokeModal(false);
      setRevokeAssignmentId(null);

      setAssignPage(1);
      await fetchAssignedUsers(1, assignPageSize);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to revoke assignment.';
      setAssignError(message);
    } finally {
      setIsRevoking(false);
    }
  }, [fetchAssignedUsers, revokeAssignmentId]);
  
  // Fetch promo data
  useEffect(() => {
    const fetchPromo = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        const promoData = await getPromoById(id);
        setPromo(promoData);
      } catch (err: any) {
        setError(err.message || 'Failed to load promo details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPromo();
  }, [id]);

  // Fetch assigned users only when Assignments tab is active
  useEffect(() => {
    if (!promo?.id) return;
    if (activeTab !== 'assignments') return;
    fetchAssignedUsers(assignPage, assignPageSize);
  }, [promo?.id, activeTab, assignPage, assignPageSize, fetchAssignedUsers]);

  const isAssignedVisibility = promo?.visibility?.type === PromoVisibilityType.ASSIGNED;

  // If someone navigates to Assignments tab while promo isn't ASSIGNED,
  // fall back to Overview.
  useEffect(() => {
    if (!promo) return;
    if (!isAssignedVisibility && activeTab === 'assignments') {
      setActiveTab('overview');
    }
  }, [isAssignedVisibility, activeTab]);
  
  // Handle edit navigation
  const handleEdit = () => {
    if (promo) {
      navigate(`/promos/promos/${promo.id}/edit`, {
        state: { fromDetail: true }
      });
    }
  };
  
  // Handle toggle status (Activate/Deactivate)
  const handleToggleStatus = async () => {
    if (!promo) return;
    
    try {
      setError(null);
      const newStatus = promo.status === PromoStatus.ACTIVE 
        ? PromoStatus.INACTIVE 
        : PromoStatus.ACTIVE;
      
      await updatePromoStatus(promo.id, newStatus);
      
      // Refresh promo data
      const updatedPromo = await getPromoById(promo.id);
      setPromo(updatedPromo);
      
      const action = newStatus === PromoStatus.ACTIVE ? 'Activated' : 'Deactivated';
      setSuccessTitle(`Promo ${action}`);
      setSuccessMessage(`Promo "${promo.name}" has been ${action.toLowerCase()} successfully.`);
      setShowSuccessModal(true);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.error || 
                          err?.message || 
                          'Failed to update promo status. Please try again.';
      setError(errorMessage);
    }
  };

  const handleOpenAssignModal = () => {
    setAssignError(null);
    setEditingAssignmentId(null);
    setAssignForm({
      userId: '',
      usageLimit: 1,
      expiresAt: '',
    });
    setShowAssignModal(true);
  };

  const toDateInputValue = (value?: string) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  const handleOpenEditAssignmentModal = (row: {
    assignmentId: string;
    userId: string;
    usageLimit: number;
    expiresAt: string;
  }) => {
    setAssignError(null);
    setEditingAssignmentId(row.assignmentId);
    setAssignForm({
      userId: row.userId,
      usageLimit: row.usageLimit ?? 1,
      expiresAt: toDateInputValue(row.expiresAt),
    });
    setShowAssignModal(true);
  };

  const handleAssignPromo = async () => {
    if (!promo?.id) return;
    if (!editingAssignmentId && !assignForm.userId.trim()) {
      setAssignError('User ID is required.');
      return;
    }

    if (!assignForm.usageLimit || assignForm.usageLimit <= 0) {
      setAssignError('Usage limit must be greater than 0.');
      return;
    }
    if (!assignForm.expiresAt) {
      setAssignError('Expiry date is required.');
      return;
    }

    try {
      setIsAssigning(true);
      setAssignError(null);

      if (editingAssignmentId) {
        // Update assignment (edit mode)
        await secureApi.post(
          `/api/v1/promo/assignments/${editingAssignmentId}/update`,
          {
            usageLimit: Number(assignForm.usageLimit),
            expiresAt: assignForm.expiresAt,
          },
        );
        setSuccessTitle('Assignment Updated');
        setSuccessMessage(`Promo assignment updated successfully.`);
      } else {
        // Create assignment (assign mode)
        await secureApi.post('/api/v1/promo/assignments', {
          promoId: promo.id,
          userId: assignForm.userId.trim(),
          usageLimit: Number(assignForm.usageLimit),
          expiresAt: assignForm.expiresAt,
        });
        setSuccessTitle('Promo Assigned');
        setSuccessMessage(`Promo "${promo.name}" assigned successfully.`);
      }

      setShowAssignModal(false);
      setShowSuccessModal(true);
      setEditingAssignmentId(null);
      setAssignPage(1);
      await fetchAssignedUsers(1, assignPageSize);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to assign promo. Please try again.';
      setAssignError(errorMessage);
    } finally {
      setIsAssigning(false);
    }
  };
  
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <ShimmerLoader type="table" rows={5} />
        </Container>
      </div>
    );
  }
  
  if (error || !promo) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert variant="danger" className="m-3">
            {error || 'Promo not found'}
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Promos', onClick: () => navigate('/promos') },
              { label: 'All Promos', onClick: () => navigate('/promos/promos') },
              { label: promo.name || 'Promo Detail', active: true }
            ]} 
          />
        </div>
        
        {/* Header */}
        <div className="mb-4" style={{ padding: '0 15px' }}>
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-3 mb-2">
                <button
                  onClick={() => navigate('/promos/promos')}
                  className="btn btn-link p-0 text-decoration-none"
                  style={{ color: '#6B7280', fontSize: '20px' }}
                >
                  <i className="ph-arrow-left"></i>
                </button>
                <h1 style={{ fontWeight: 600, fontSize: 24, marginBottom: 0, lineHeight: 1.2 }}>
                  {promo.name}
                </h1>
                <PromoStatusBadge status={promo.status as 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'EXPIRED' | 'INACTIVE'} />
              </div>
              <div className="ms-5" style={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
                {promo.code || 'N/A'} • {promo.domain} • {promo.transactionType}
              </div>
            </div>
            
            {/* Action Buttons - only for manage/admin permissions */}
            {hasManagePermissions && (
              <div className="d-flex gap-2">
                <button
                  onClick={handleEdit}
                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                  style={{
                    height: '40px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  <i className="ph-pencil"></i>
                  Edit
                </button>
                <button
                  onClick={handleToggleStatus}
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  style={{
                    height: '40px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                  disabled={promo.status === PromoStatus.ARCHIVED}
                >
                  <i className={promo.status === PromoStatus.ACTIVE ? 'ph-pause' : 'ph-play'}></i>
                  {promo.status === PromoStatus.ACTIVE ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Tabs */}
        <div style={{ padding: '0 15px' }}>
          <Nav variant="tabs" className="tournament-tabs mb-4 d-flex align-items-center">
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'overview'}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'usage'}
                  onClick={() => setActiveTab('usage')}
                >
                  Usage
                </Nav.Link>
              </Nav.Item>
              {isAssignedVisibility && (
                <Nav.Item>
                  <Nav.Link
                    className="tournament-tab"
                    active={activeTab === 'assignments'}
                    onClick={() => setActiveTab('assignments')}
                  >
                    Assignments
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'audit-logs'}
                  onClick={() => setActiveTab('audit-logs')}
                >
                  Audit Logs
                </Nav.Link>
              </Nav.Item>
              {isAssignedVisibility && activeTab === 'assignments' && (
                <div className="ms-auto">
                  <Button
                    variant="primary"
                    onClick={handleOpenAssignModal}
                    disabled={!hasManagePermissions}
                  >
                    + Assign Promo
                  </Button>
                </div>
              )}
            </Nav>
        </div>
        
        {/* Tab Content */}
        <div style={{ padding: '0 15px' }}>
          {activeTab === 'overview' && <OverviewTab promo={promo} />}
          {activeTab === 'usage' && <UsageTab promoId={promo.id} emptyMessage="No usage data found for this promo." />}
          {activeTab === 'assignments' && isAssignedVisibility && (
            <Card>
              <Card.Body>
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ fontWeight: 600, fontSize: '13px', color: '#6B7280' }}>User</th>
                        <th style={{ fontWeight: 600, fontSize: '13px', color: '#6B7280' }}>User ID</th>
                        <th style={{ fontWeight: 600, fontSize: '13px', color: '#6B7280' }}>Status</th>
                        <th style={{ fontWeight: 600, fontSize: '13px', color: '#6B7280' }}>Used</th>
                        <th style={{ fontWeight: 600, fontSize: '13px', color: '#6B7280' }}>Expires</th>
                        <th style={{ fontWeight: 600, fontSize: '13px', color: '#6B7280', width: 90 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignmentRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            No assignments found.
                          </td>
                        </tr>
                      ) : (
                        assignmentRows.map((row) => (
                          <tr key={row.id}>
                            <td>{row.fullName || row.userId}</td>
                            <td>{row.userId || '—'}</td>
                            <td>
                              <span
                                className={`badge ${
                                  row.status === 'REVOKED'
                                    ? 'bg-danger-subtle text-danger'
                                    : 'bg-success-subtle text-success'
                                }`}
                              >
                                {row.status}
                              </span>
                            </td>
                            <td>{row.used}/{row.usageLimit}</td>
                            <td>{formatExpiresAt(row.expiresAt)}</td>
                            <td>
                              <div
                                style={{ position: 'relative', width: 'fit-content' }}
                              >
                                <span
                                  className="actions-icon"
                                  style={{
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    color: '#6B7280',
                                    display: 'inline-block',
                                    lineHeight: '1',
                                    userSelect: 'none',
                                  }}
                                  onClick={(e) => handleActionIconClick(row.id, e)}
                                >
                                  &#8942;
                                </span>
                                {hoveredActionRowId === row.id &&
                                  actionDropdownPosition &&
                                  createPortal(
                                    <div
                                      className="dropdown-menu-actions"
                                      ref={actionDropdownRef}
                                      style={{
                                        position: 'fixed',
                                        top: actionDropdownPosition.y,
                                        left: actionDropdownPosition.x,
                                        minWidth: 120,
                                        zIndex: 999999,
                                        padding: 0,
                                      }}
                                    >
                                      {row.status !== 'REVOKED' && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => {
                                            setRevokeAssignmentId(row.assignmentId);
                                            setShowRevokeModal(true);
                                            setHoveredActionRowId(null);
                                            setActionDropdownPosition(null);
                                          }}
                                        >
                                          Revoke
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                          handleOpenEditAssignmentModal({
                                            assignmentId: row.assignmentId,
                                            userId: row.userId,
                                            usageLimit: row.usageLimit,
                                            expiresAt: row.expiresAt,
                                          });
                                          setHoveredActionRowId(null);
                                          setActionDropdownPosition(null);
                                        }}
                                      >
                                        Edit
                                      </button>
                                    </div>,
                                    document.body,
                                  )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <TablePaginationFooter
                  page={assignPage}
                  totalPages={Math.max(1, assignTotalPages)}
                  pageSize={assignPageSize}
                  totalCount={assignTotalCount}
                  onPageChange={(p) => setAssignPage(p)}
                  onPageSizeChange={(size) => {
                    setAssignPageSize(size);
                    setAssignPage(1);
                  }}
                />
              </Card.Body>
            </Card>
          )}
          {activeTab === 'audit-logs' && <AuditLogsTab promoId={promo.id} emptyMessage="No audit logs found for this promo." />}
        </div>
        
        {/* Error Alert */}
        {error && (
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </div>
        )}
        
        {/* Success Modal */}
        <SuccessModal
          show={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          title={successTitle}
          message={successMessage}
        />

        {/* Revoke confirmation modal */}
        <DeleteModal
          show={showRevokeModal}
          handleClose={() => {
            setShowRevokeModal(false);
            setRevokeAssignmentId(null);
          }}
          deleteModalFunction={handleConfirmRevoke}
          title="Revoke Assignment"
          message="Are you sure you want to revoke this promo assignment? This action cannot be undone."
          confirmLabel={isRevoking ? 'Revoking...' : 'Yes, Revoke'}
          cancelLabel="Cancel"
          isLoading={isRevoking}
          error={assignError}
          showIcon
        />

        <Modal
          show={showAssignModal}
          onHide={() => {
            setShowAssignModal(false);
            setEditingAssignmentId(null);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingAssignmentId ? 'Edit Promo Assignment' : 'Assign Promo'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {assignError && (
              <Alert variant="danger" className="mb-3">
                {assignError}
              </Alert>
            )}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>User ID <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={assignForm.userId}
                  disabled={!!editingAssignmentId}
                  onChange={(e) => setAssignForm(prev => ({ ...prev, userId: e.target.value }))}
                  placeholder="Enter user ID"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Usage Limit <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={assignForm.usageLimit}
                  onChange={(e) => setAssignForm(prev => ({ ...prev, usageLimit: Number(e.target.value) || 0 }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Expires At <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={assignForm.expiresAt}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setAssignForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAssignModal(false);
                setEditingAssignmentId(null);
              }}
              disabled={isAssigning}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAssignPromo} disabled={isAssigning}>
              {isAssigning ? (editingAssignmentId ? 'Updating...' : 'Assigning...') : (editingAssignmentId ? 'Update' : 'Assign')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default PromoDetailPage;
